import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Avatar,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowForwardIos, ArrowBackIos, HelpOutline, Close, Send } from '@mui/icons-material';
import { Carousel } from 'antd';
import { PublicClientApplication } from '@azure/msal-browser';

const testimonials = [
  {
    text: 'Discover endless opportunities with Cognitivo for my financial freedom where innovation meets opportunity to empower my future',
    name: 'Daphne Park',
    title: 'Business Person',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
  },
  {
    text: 'Cognitivo helped me organize my finances and plan for the future with confidence!',
    name: 'Alex Kim',
    title: 'Entrepreneur',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
  },
  {
    text: 'A seamless experience for managing my investments and goals.',
    name: 'Priya Singh',
    title: 'Investor',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
  },
];

const backgroundImg = '/loginbg.png';

// MSAL configuration (replace YOUR_CLIENT_ID with your Azure AD Application (client) ID)
const msalConfig = {
  auth: {
    clientId: 'YOUR_CLIENT_ID', // <-- Replace with your Azure AD Application (client) ID
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: window.location.origin,
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

const CODE_LENGTH = 6;

const NewLoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [show2FA, setShow2FA] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [showChat, setShowChat] = useState(false);
  const [chatInput, setChatInput] = useState('');

  const handleNext = () => setCarouselIndex((prev) => (prev + 1) % testimonials.length);
  const handleBack = () => setCarouselIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  const handleMicrosoftSignIn = async () => {
    try {
      await msalInstance.initialize(); // Ensure MSAL is initialized before loginPopup
      await msalInstance.loginPopup({
        scopes: ['user.read'],
      });
      // You can now get the account info or token from msalInstance
      // Redirect or update your app state as needed
    } catch (error) {
      console.error('Microsoft login error:', error);
    }
  };

  const handleInputChange = (idx: number, value: string) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newCode = [...code];
    newCode[idx] = value;
    setCode(newCode);
    if (value && idx < CODE_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  // Chatbox send handler (UI only)
  const handleSendChat = (e: React.FormEvent) => {
    e.preventDefault();
    setChatInput('');
  };

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        bgcolor: '#f5f6fa',
        position: 'relative',
      }}
    >
      {/* Left Side - Image with Carousel Overlay */}
      <Box
        sx={{
          width: '50vw',
          height: '100vh',
          position: 'relative',
          bgcolor: '#eee',
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Box
          component="img"
          src={backgroundImg}
          alt="login background"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 0,
            display: 'block',
          }}
        />
        {/* Carousel Overlay */}
        <Box
  sx={{
    position: 'absolute',
    top: '80%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 400,
    bgcolor: 'white',
    color: 'black',
    borderRadius: 3,
    p: 3,
    boxShadow: 3,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    textAlign: 'left',
  }}
>

          <Carousel
            dots={true}
            beforeChange={(_, next) => setCarouselIndex(next)}
            afterChange={setCarouselIndex}
            effect="scrollx"
            draggable
            style={{ minHeight: 100 }}
            initialSlide={carouselIndex}
            arrows={false}
          >
            {testimonials.map((item, idx) => (
              <Box key={idx}>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 400 }}>
                  {item.text}
                </Typography>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar src={item.avatar} alt={item.name} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="grey.300">
                      {item.title}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Carousel>
        </Box>
      </Box>
      {/* Right Side - Login, 2FA, or Forgot Password Form */}
      <Box
        sx={{
          width: '50vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          pl: { xs: 0, md: 10 },
          pr: { xs: 0, md: 8 },
          bgcolor: 'white',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
          {!show2FA && !showForgotPassword ? (
            <>
              <Typography variant="h5" fontWeight={700} mb={1.5}>
                Welcome to Cognitivo <span role="img" aria-label="wave">👋</span>
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Today is a new day. It's your day. You shape financial planning
              </Typography>
              <Box component="form" mb={2}>
                <TextField
                  label="Email"
                  type="email"
                  placeholder="Example@email.com"
                  fullWidth
                  margin="normal"
                  size="small"
                  autoComplete="email"
                />
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 8 characters"
                  fullWidth
                  margin="normal"
                  size="small"
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((show) => !show)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} mb={2}>
                  <FormControlLabel
                    control={<Checkbox size="small" />}
                    label={<Typography variant="body2">Remember me</Typography>}
                  />
                  <Button variant="text" size="small" sx={{ textTransform: 'none' }} onClick={() => setShowForgotPassword(true)}>
                    Forgot Password?
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ py: 1.2, borderRadius: 2, fontWeight: 600, fontSize: '1rem' }}
                  onClick={() => setShow2FA(true)}
                >
                  Sign in
                </Button>
              </Box>
              <Divider sx={{ my: 2 }}>Or</Divider>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<img src="https://img.icons8.com/color/48/000000/microsoft.png" alt="Microsoft" width={22} height={22} style={{ marginRight: 8 }} />}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#f5f6fa',
                  borderRadius: 2,
                  fontWeight: 500,
                  mb: 2,
                }}
                onClick={handleMicrosoftSignIn}
              >
                Sign in with Microsoft
              </Button>
              <Box textAlign="center" mt={1}>
                <Typography variant="body2" color="text.secondary">
                  Don't you have an account?{' '}
                  <Button variant="text" size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }}>
                    Sign up
                  </Button>
                </Typography>
              </Box>
              <Box flexGrow={1} />
              <Box textAlign="center" mt={4}>
                <Typography variant="caption" color="text.disabled">
                  © 2025 ALL RIGHTS RESERVED
                </Typography>
              </Box>
            </>
          ) : show2FA ? (
            <>
              <Typography variant="h5" fontWeight={700} mb={2}>
                We've sent you a code
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={4}>
                To continue account login, enter the code we've sent to your mobile phone via SMS
              </Typography>
              <Box display="flex" justifyContent="center" gap={2} mb={3}>
                {code.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={el => (inputRefs.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleInputChange(idx, e.target.value)}
                    onKeyDown={e => handleKeyDown(idx, e)}
                    style={{
                      width: 44,
                      height: 44,
                      fontSize: 24,
                      textAlign: 'center',
                      border: '1.5px solid #d1d5db',
                      borderRadius: 8,
                      outline: 'none',
                      background: '#f7f7fa',
                    }}
                  />
                ))}
              </Box>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ py: 1.2, borderRadius: 2, fontWeight: 600, fontSize: '1rem', mb: 2 }}
              >
                Verify
              </Button>
              <Box display="flex" justifyContent="center" alignItems="center" gap={1} mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Don't get the code?
                </Typography>
                <Link href="#" underline="hover" sx={{ fontWeight: 500, cursor: 'pointer' }}>
                  Resend It
                </Link>
              </Box>
              <Box display="flex" justifyContent="center" mb={4}>
                <Link href="#" underline="hover" sx={{ fontWeight: 500, cursor: 'pointer' }} onClick={() => setShow2FA(false)}>
                  Back
                </Link>
              </Box>
              <Box flexGrow={1} />
              <Box textAlign="center" mt={4}>
                <Typography variant="caption" color="text.disabled">
                  © 2025 ALL RIGHTS RESERVED
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h5" fontWeight={700} mb={2}>
                Forgot Password
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={4}>
                Enter your email to receive reset link
              </Typography>
              <TextField
                label="Email"
                type="email"
                placeholder="Example@email.com"
                fullWidth
                margin="normal"
                size="small"
                autoComplete="email"
                sx={{ mb: 3 }}
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ py: 1.2, borderRadius: 2, fontWeight: 600, fontSize: '1rem', mb: 2 }}
              >
                Confirm
              </Button>
              <Box display="flex" justifyContent="center" mb={4}>
                <Link href="#" underline="hover" sx={{ fontWeight: 500, cursor: 'pointer' }} onClick={() => setShowForgotPassword(false)}>
                  Back
                </Link>
              </Box>
              <Box flexGrow={1} />
              <Box textAlign="center" mt={4}>
                <Typography variant="caption" color="text.disabled">
                  © 2025 ALL RIGHTS RESERVED
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
      {/* Floating Help Icon and Chatbox */}
      <Box sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1200 }}>
        {!showChat && (
          <IconButton
            sx={{
              bgcolor: 'white',
              boxShadow: 3,
              width: 48,
              height: 48,
              borderRadius: '50%',
              border: '1.5px solid #e0e0e0',
              '&:hover': { bgcolor: '#f5f6fa' },
            }}
            onClick={() => setShowChat(true)}
          >
            <HelpOutline sx={{ color: '#1976d2', fontSize: 28 }} />
          </IconButton>
        )}
        {showChat && (
          <Box
            sx={{
              width: 320,
              minHeight: 260,
              maxHeight: 420,
              bgcolor: 'white',
              borderRadius: 3,
              boxShadow: 8,
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Chat header */}
            <Box sx={{
              bgcolor: 'linear-gradient(180deg, #6CCBDA 0%, #1976d2 100%)',
              background: 'linear-gradient(180deg, #6CCBDA 0%, #1976d2 100%)',
              color: 'white',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: 600,
              fontSize: 16,
            }}>
              <span>Hello there 👋<br /><span style={{ fontWeight: 400, fontSize: 14 }}>How can we help you?</span></span>
              <IconButton size="small" sx={{ color: 'white' }} onClick={() => setShowChat(false)}>
                <Close />
              </IconButton>
            </Box>
            {/* Chat body (empty for now) */}
            <Box sx={{ flex: 1, px: 2, py: 1, bgcolor: 'transparent' }} />
            {/* Chat input */}
            <Box component="form" onSubmit={handleSendChat} sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, borderTop: '1px solid #e0e0e0', bgcolor: '#f7f7fa' }}>
              <input
                type="text"
                placeholder="Send us a message"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: 15,
                  padding: '8px 0',
                }}
              />
              <IconButton type="submit" sx={{ color: '#1976d2' }}>
                <Send />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NewLoginPage;
